import { Checkbox, CopyTooltip, TableBadge } from '@serodigital/ui-components';
import * as React from 'react';
import { useCallback } from 'react';
import { BeeStatus } from '../../../shared/api-clients/_api';
import { Bee } from '../../../shared/api-clients/gql';
import { TableTooltip } from '../../../shared/components/TableTooltip';
import { percentageOfValue } from '../../../shared/helpers/mathHelpers';
import { UIConfig } from '../helpers/UIConfig';
import { equinoxLabels } from '../views/bee/details';
import { TrialName } from '../../../../backend/shared/types/trial';

export const trialBadgeClassname = (inActiveTrialEvent: boolean) => {
  return {
    [TrialName.EQUINOX_GROUP_A]: inActiveTrialEvent ? 'sero-bg-common-success' : 'sero-bg-blue-1-dark',
    [TrialName.EQUINOX_GROUP_B]: inActiveTrialEvent ? 'sero-bg-common-success' : 'sero-bg-green-9-dark',
  };
};

export function useTableDefinition() {
  const generateTableDefinition = useCallback(
    (setSelectedBees, selectedBees, showCheckboxes, selectedHardwareVersion, windowWidth = 100) => {
      const columns = [
        {
          Header: 'BEE ID',
          accessor: 'deviceId',
          Cell: ({ row: { original: bee } }: { row: { original: Bee } }) => {
            return (
              <CopyTooltip
                label={bee?.deviceId}
                placement="top"
                hasArrow={true}
                onCopyComplete={() => {}}
                value={bee?.deviceId}
                closeOnCopy={true}
              >
                <p className="custom-truncate">{bee?.deviceId}</p>
              </CopyTooltip>
            );
          },
          width: percentageOfValue(windowWidth, 9),
        },
        {
          Header: 'MAC address',
          accessor: 'routerId',
          width: percentageOfValue(windowWidth, 14),
        },
        {
          Header: 'Address',
          accessor: 'physicalAddress',
          Cell: ({ row: { original: bee } }: { row: { original: Bee } }) => {
            return <TableTooltip label={bee.physicalAddress?.addressLine1} />;
          },
          width: percentageOfValue(windowWidth, 18),
        },
        {
          Header: 'Plot',
          accessor: 'plot',
          Cell: ({ row: { original: bee } }: { row: { original: Bee } }) => {
            return <p>{bee.plot?.split('Plot').pop()}</p>;
          },
          width: percentageOfValue(windowWidth, 5),
        },
        {
          Header: 'Project',
          accessor: 'project',
          Cell: ({ row: { original: bee } }: { row: { original: Bee } }) => {
            return <TableTooltip label={bee.project} />;
          },
          width: percentageOfValue(windowWidth, 10),
        },
        {
          Header: 'Healthcheck',
          accessor: 'arePeripheralsHealthy',
          Cell: ({ row: { original: bee } }: { row: { original: Bee } }) => {
            return bee.arePeripheralsHealthy ? (
              <TableBadge kind="complete" customLabel="Pass" />
            ) : (
              <TableBadge kind="none" className="sero-bg-common-error" customLabel="Fail" />
            );
          },
          width: percentageOfValue(windowWidth, 8),
        },
        {
          Header: 'Status',
          accessor: 'status',
          Cell: ({ row: { original: bee } }: { row: { original: Bee } }) => {
            return bee.status === 'Online' ? (
              <p>{bee.status}</p>
            ) : (
              <p className="sero-text-brand-error">{bee.status ?? 'Offline'}</p>
            );
          },
          width: percentageOfValue(windowWidth, 6),
        },
        {
          Header: 'Firmware Version',
          accessor: 'currentFirmwareVersion',
          width: percentageOfValue(windowWidth, 10),
        },
        {
          Header: 'Trials',
          accessor: 'trialId',
          Cell: ({ row: { original: bee } }: { row: { original: Bee } }) => {
            if (bee.trial === null || bee.trial === undefined) {
              return <p data-qa={`${bee.deviceId}-no-trial-badge`}>None</p>;
            }

            return (
              <TableBadge
                kind={bee.inActiveTrialEvent ? 'complete' : 'none'}
                customLabel={equinoxLabels[bee.trial.name]}
                className={trialBadgeClassname(bee.inActiveTrialEvent)[bee.trial.name]}
                data-qa={`${bee.deviceId}-trial-badge`}
              />
            );
          },
          width: percentageOfValue(windowWidth, 6),
        },
      ];
      if (showCheckboxes) {
        columns.unshift({
          Header: '',
          accessor: 'checkbox',
          Cell: ({ row: { original: bee } }: { row: { original: Bee } }) => {
            const checkboxDisabled =
              (selectedBees?.length >= UIConfig.selectedBEEsLimit && !selectedBees.includes(bee.deviceId)) ||
              bee.status !== BeeStatus.Online ||
              (selectedHardwareVersion && selectedHardwareVersion !== bee.hardwareVersion);

            const onClick = (e) => {
              e.stopPropagation();
            };
            return (
              <div onClick={onClick} onKeyDown={onClick} className={'flex justify-center items-center h-[66px] mr-20'}>
                <Checkbox
                  key={bee.deviceId}
                  id={`list-bees-row-checkbox-${bee.deviceId}`}
                  data-qa={`list-bees-row-checkbox-${bee.deviceId}`}
                  checked={selectedBees.includes(bee.deviceId)}
                  defaultColour={'enabled'}
                  disabled={checkboxDisabled}
                  onChange={(selected) => setSelectedBees(bee, selected)}
                />
              </div>
            );
          },
          width: percentageOfValue(windowWidth, 7),
        });
      }
      return columns;
    },
    [],
  );

  return {
    generateTableDefinition,
  };
}
